/*
 * Marketing, Campaigns as React Query hooks. Each change refreshes the list and the campaign's own page.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Audience,
  CampaignsAnswer,
  CustomBody,
  CustomCount,
  CustomPage,
  CustomTemplate,
  OfferPage,
  OfferPatch,
  PcalPage,
  PcalPatch,
  PcalPreview,
  PchkPage,
  PchkPatch,
  RfuPage,
  RfuPatch,
} from "@steward/shared";
import { api } from "./client";

function useChange<V, T>(keys: string[], run: (v: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
}

export const useCampaigns = (companyId: string) =>
  useQuery({
    queryKey: ["camp-list", companyId],
    queryFn: () => api<CampaignsAnswer>("GET", "marketing/campaigns"),
    refetchInterval: 20_000,
  });
export const useRenameCampaign = () =>
  useChange(["camp-list"], (v: { id: string; name: string }) =>
    api<CampaignsAnswer>("PUT", `marketing/campaigns/${v.id}/name`, { name: v.name }),
  );

export const usePcal = (companyId: string, on = true) =>
  useQuery({
    queryKey: ["camp-pcal", companyId],
    queryFn: () => api<PcalPage>("GET", "marketing/campaigns/pcal"),
    enabled: on,
    refetchInterval: 20_000,
  });
export const useSavePcal = () =>
  useChange(["camp-pcal", "camp-list"], (v: PcalPatch) => api<PcalPage>("PATCH", "marketing/campaigns/pcal", v));
export const usePcalPreview = () =>
  useMutation({ mutationFn: () => api<PcalPreview>("POST", "marketing/campaigns/pcal/preview") });

export const useRfu = (companyId: string, on = true) =>
  useQuery({ queryKey: ["camp-rfu", companyId], queryFn: () => api<RfuPage>("GET", "marketing/campaigns/rfu"), enabled: on });
export const useSaveRfu = () =>
  useChange(["camp-rfu", "camp-list"], (v: RfuPatch) => api<RfuPage>("PATCH", "marketing/campaigns/rfu", v));

export const usePchk = (companyId: string, on = true) =>
  useQuery({ queryKey: ["camp-pchk", companyId], queryFn: () => api<PchkPage>("GET", "marketing/campaigns/pchk"), enabled: on });
export const useSavePchk = () =>
  useChange(["camp-pchk", "camp-list"], (v: PchkPatch) => api<PchkPage>("PATCH", "marketing/campaigns/pchk", v));

export const useOffer = (companyId: string, on = true) =>
  useQuery({
    queryKey: ["camp-offer", companyId],
    queryFn: () => api<OfferPage>("GET", "marketing/campaigns/offer"),
    enabled: on,
  });
export const useSaveOffer = () =>
  useChange(["camp-offer", "camp-list"], (v: OfferPatch) => api<OfferPage>("PATCH", "marketing/campaigns/offer", v));

export const useCustom = (companyId: string, key: string) =>
  useQuery({
    queryKey: ["camp-custom", companyId, key],
    queryFn: () => api<CustomPage>("GET", `marketing/campaigns/custom/${key}`),
  });
export const useCreateCustom = () =>
  useChange(["camp-list"], (template: CustomTemplate) => api<CustomPage>("POST", "marketing/campaigns/custom", { template }));
export const useSaveCustom = () =>
  useChange(["camp-custom", "camp-list"], (v: { key: string; body: CustomBody }) =>
    api<CustomPage>("PUT", `marketing/campaigns/custom/${v.key}`, v.body),
  );
export const useDeleteCustom = () =>
  useChange(["camp-list"], (key: string) => api<CampaignsAnswer>("DELETE", `marketing/campaigns/custom/${key}`));
/** Who a set of rules picks now, while the rules are being changed. Saves nothing. */
export const useCustomCount = (companyId: string, a: Audience) =>
  useQuery({
    queryKey: ["camp-custom-count", companyId, JSON.stringify(a)],
    queryFn: () => api<CustomCount>("POST", "marketing/campaigns/custom-count", { audience: a }),
    placeholderData: (prev) => prev,
  });
